import * as React from 'react'

import { ChevronDown } from 'lucide-react'

import { cn } from '@/lib/utils'
import { FIELD_SURFACE_CLASS } from '~/client/src/shared/components/shadcn-ui/fieldSurface'

// `!flex` / `!min-w-0` / `!flex-1` because the legacy `styles.scss` is imported
// after Tailwind and would otherwise win on equal specificity.
const TRIGGER =
  'tw:!flex tw:w-full tw:min-w-0 tw:overflow-hidden tw:active:opacity-50'

const VALUE = 'tw:!min-w-0 tw:!flex-1 tw:truncate tw:text-left'

const DISPLAY_ONLY = 'tw:disabled:cursor-default tw:disabled:opacity-100'

const SIZE = {
  default: 'tw:h-8',
  lg: 'tw:h-9',
} as const

interface IProps extends React.ComponentPropsWithoutRef<'button'> {
  /** Trailing affordance. Sized by the surface; pass `false` for none. */
  icon?: React.ReactNode | false
  /** `default` is 32px; `lg` is a 36px touch target. */
  size?: keyof typeof SIZE
  /**
   * Shows the value with the field's normal look but no trailing affordance and no
   * interaction - for a value the viewer may see but not change. Not a permission:
   * the caller decides who gets it.
   */
  displayOnly?: boolean
}

/**
 * A field-shaped trigger: the same surface as `SelectTrigger`, for a control
 * that opens something or navigates rather than accepting text. Compose it as a
 * base-ui trigger with `render={<FieldTrigger />}` — passing base-ui's own
 * `disabled`/`id` to the *trigger*, not here — or use it directly as a button.
 * The value is `children`; it truncates.
 */
const FieldTrigger = React.forwardRef<HTMLButtonElement, IProps>(
  (
    { icon, size = 'default', displayOnly, className, children, ...props },
    ref,
  ) => (
    <button
      {...props}
      disabled={displayOnly || props.disabled}
      ref={ref}
      type="button"
      data-slot="field-trigger"
      className={cn(
        FIELD_SURFACE_CLASS,
        TRIGGER,
        SIZE[size],
        displayOnly && DISPLAY_ONLY,
        className,
      )}
    >
      <span className={VALUE}>{children}</span>
      {icon === false || displayOnly ? null : (icon ?? <ChevronDown />)}
    </button>
  ),
)
FieldTrigger.displayName = 'FieldTrigger'

export default FieldTrigger
